import React, { useEffect, useMemo, useState, Fragment } from 'react';
import { IRestriction, IRestrictionsAreaComponent } from 'src/pages/observers/components/RestrictionsArea/types';
import {
	FieldRestrictionFormItemContainer,
	GrowFormItem,
	OperatorFormItem,
	OrSeparator,
	RestrictionFormContainer,
	RestrictionFormItem,
	RestrictionTypeFormItem,
	RowRestrictionFormItemContainer
} from 'src/pages/observers/components/RestrictionsArea/styles';
import { MinusCircleOutlined } from '@ant-design/icons';
import { Select, InputNumber, Button, Input, Flex } from 'antd';
import { FieldRestriction, RowRestriction } from 'src/interfaces/entities/observer';
import { toast } from 'react-toastify';

export default function RestrictionsArea(
	{
		disabled,
		queryFields,
		onRestrictionsChange,
		defaultFieldRestrictions,
		defaultRowRestrictions,
	}: IRestrictionsAreaComponent
) {
	const [ restrictions, setRestrictions, ] = useState<IRestriction[]>([]);

	const fieldsWithTypes = useMemo(() => {
		return queryFields.flatMap(({ field, type, }) => {
			switch (type.toUpperCase()) {
				case 'INT':
				case 'INTEGER':
				case 'BIGINT':
				case 'SERIAL':
				case 'SMALLINT':
				case 'FLOAT':
				case 'DECIMAL':
				case 'NUMBER':
					return { field, type: 'number', };
				case 'VARCHAR':
				case 'VARCHAR2':
				case 'CHAR':
				case 'TEXT':
					return { field, type: 'string', };
				case 'BOOLEAN':
				case 'BOOL':
					return { field, type: 'boolean', };
				default:
					return [];
			}
		});
	}, [ queryFields, ]);

	useEffect(() => {
		const jsonFields: FieldRestriction[] = defaultFieldRestrictions as FieldRestriction[];
		const fieldRestrictions = (jsonFields ?? [])
			.map((el) => {
				const dataType = typeof el.value === 'number' ? 'number' : 'string';
				return {
					type: 'field',
					rowRestriction: {
						type: '',
						value: undefined,
					},
					fieldRestriction: {
						dataType,
						type: el.type,
						field: el.field,
						value: el.value,
					},
				};
			});

		const jsonRows: RowRestriction[] = defaultRowRestrictions as RowRestriction[];
		const rowRestrictions = (jsonRows ?? [])
			.map((el) => {
				return {
					type: 'row',
					rowRestriction: {
						type: el.type,
						value: el.value,
					},
					fieldRestriction: {
						dataType: 'string',
						type: 'eq',
						field: undefined,
						value: undefined,
					},
				};
			});

		const restrictionsArray = [ ...rowRestrictions, ...fieldRestrictions, ];
		if (restrictionsArray !== restrictions) {
			setRestrictions(restrictionsArray as IRestriction[]);
		}

	}, [ defaultFieldRestrictions, defaultRowRestrictions, ]);

	useEffect(() => {
		const field_restrictions = restrictions.flatMap((el) => {
			if (
				el.type === 'field'
				&& el.fieldRestriction.field
				&& el.fieldRestriction.type
				&& el.fieldRestriction.value !== undefined
				&& el.fieldRestriction.value !== null
			) {
				return {
					field: el.fieldRestriction.field,
					type: el.fieldRestriction.type,
					value: el.fieldRestriction.value,
				};
			}

			return [];
		});

		const row_restrictions = restrictions.flatMap((el) => {
			if (
				el.type === 'row'
				&& el.rowRestriction.type
				&& el.rowRestriction.value !== undefined
				&& el.rowRestriction.value !== null
			) {
				return {
					type: el.rowRestriction.type,
					value: el.rowRestriction.value,
				};
			}

			return [];
		});

		onRestrictionsChange?.(field_restrictions, row_restrictions);
	}, [ restrictions, ]);

	const handleAddRestriction = () => {
		setRestrictions((prev) => {
			const newRestriction: IRestriction = {
				type: 'row',
				fieldRestriction: {
					type: 'eq',
				},
				rowRestriction: {
					type: 'rows-eq',
				},
			};
			return [ ...prev, newRestriction, ];
		});
	};

	const handleRemoveRestriction = (index: number) => {
		setRestrictions((prev) => {
			const _restrictions = [ ...prev, ];
			_restrictions.splice(index, 1);

			return _restrictions;
		});
	};

	const handleRestrictionTypeChange = (
		index: number,
		type: 'row' | 'field'
	) => {
		setRestrictions((prev) => {
			const _restrictions = [ ...prev, ];
			const restriction = _restrictions[index];
			if (!restriction) return _restrictions;

			if (type === 'field') {
				restriction.fieldRestriction.type = 'eq';
			} else {
				restriction.rowRestriction.type = 'rows-eq';
			}

			restriction.type = type;
			return _restrictions;
		});
	};

	const handleRowRestrictionChange = (
		index: number,
		key: keyof RowRestriction,
		value: RowRestriction['type'] | number
	) => {
		setRestrictions((prev) => {
			const _restrictions = [ ...prev, ];
			const restriction = _restrictions[index];
			if (!restriction.rowRestriction) return _restrictions;

			if (key === 'value') {
				const hasConflict = restrictions.some(
					(el) =>
						[ 'rows-eq', 'rows-neq', ].includes(
							el.rowRestriction.type ?? ''
						) && el.rowRestriction.value === value 
						&& el.rowRestriction.value !== undefined
						&& value !== undefined
				);

				if (hasConflict) {
					toast.warn(
						'O valor digitado entrará em conflito com outra regra.'
					);
					return _restrictions;
				}
			}

			//@ts-ignore
			restriction.rowRestriction[key] = value;
			return _restrictions;
		});
	};

	const handleFieldRestrictionChange = (
		index: number,
		key: keyof FieldRestriction | 'dataType',
		value?: FieldRestriction['type'] | number | string
	) => {
		setRestrictions((prev) => {
			const _restrictions = [ ...prev, ];
			const restriction = _restrictions[index];
			if (!restriction.fieldRestriction) return _restrictions;

			if (key === 'value') {
				const hasConflict = restrictions.some(
					(el) => {
						const hasValue = value !== undefined && el.fieldRestriction.value !== undefined;
						const hasSameValue = el.fieldRestriction.value === value;
						const hasSameField = el.fieldRestriction.field === restriction.fieldRestriction.field;
						const isOperatorEqualOrNotEqual = [ 'eq', 'neq', ].includes(
							el.fieldRestriction.type ?? ''
						);
						return hasValue && hasSameValue && hasSameField && isOperatorEqualOrNotEqual;
					});

				if (hasConflict) {
					toast.warn(
						'O valor digitado entrará em conflito com outra regra.'
					);
				}
			}

			//@ts-ignore
			restriction.fieldRestriction[key] = value;
			return _restrictions;
		});
	};

	return (
		<Flex gap={8} vertical>
			{restrictions.map((restriction, index) => {
				return (
					<Fragment key={index}>
						{index > 0 && restrictions.length > 1 && (
							<OrSeparator>
								<span>OU</span>
							</OrSeparator>
						)}
						<RestrictionFormContainer>
							<RestrictionTypeFormItem>
								<Select
									style={{ width: '100%', }}
									value={restriction.type}
									options={[
										{
											label: 'Linha',
											value: 'row',
										},
										{
											label: 'Campo',
											value: 'field',
										},
									]}
									onSelect={(value) => {
										handleRestrictionTypeChange(
											index,
											value
										);
									}}
								/>
							</RestrictionTypeFormItem>
							{restriction.type === 'row' && (
								<RestrictionFormItem>
									<RowRestrictionFormItemContainer
										key={index}
									>
										<OperatorFormItem>
											<Select
												style={{ width: '100%', }}
												value={
													restriction.rowRestriction
														.type
												}
												options={[
													{
														label: 'Maior que',
														value: 'rows-gt',
														disabled:
														restrictions.some(
															(el) =>
																el.rowRestriction
																	.type === 'rows-gt'
														),
													},
													{
														label: 'Menor que',
														value: 'rows-ls',
														disabled:
														restrictions.some(
															(el) =>
																el.rowRestriction
																	.type === 'rows-ls'
														),
													},
													{
														label: 'Igual a',
														value: 'rows-eq',
													},
													{
														label: 'Diferente de',
														value: 'rows-neq',
													},
												]}
												onSelect={(value) => {
													handleRowRestrictionChange(
														index,
														'type',
														value
													);
												}}
											/>
										</OperatorFormItem>
										<GrowFormItem>
											<InputNumber
												value={
													restriction.rowRestriction
														.value
												}
												placeholder='...'
												style={{ width: '100%', }}
												onChange={(value) => {
													const _value =
													value as number;
													handleRowRestrictionChange(
														index,
														'value',
														_value
													);
												}}
											/>
										</GrowFormItem>
										<Button
											type='text'
											danger
											icon={<MinusCircleOutlined />}
											onClick={() => {
												handleRemoveRestriction(index);
											}}
										/>
									</RowRestrictionFormItemContainer>
								</RestrictionFormItem>
							)}
							{restriction.type === 'field' && (
								<RestrictionFormItem>
									<FieldRestrictionFormItemContainer
										key={index}
									>
										<OperatorFormItem>
											<Select
												style={{ width: '100%', }}
												value={
													restriction.fieldRestriction
														.type
												}
												options={[
													{
														label: 'Maior que',
														value: 'gt',
														disabled:
														restrictions.some(
															(el) =>
																el.fieldRestriction
																	.type === 'gt'
														) ||
														[ 'string', 'boolean', ]
															.includes(restriction.fieldRestriction.dataType ?? ''),
													},
													{
														label: 'Menor que',
														value: 'ls',
														disabled:
														restrictions.some(
															(el) =>
																el.fieldRestriction
																	.type === 'ls'
														) ||
														[ 'string', 'boolean', ]
															.includes(restriction.fieldRestriction.dataType ?? ''),
													},
													{
														label: 'Igual a',
														value: 'eq',
														disabled:
														[ 'boolean', ]
															.includes(restriction.fieldRestriction.dataType ?? ''),
													},
													{
														label: 'Diferente de',
														value: 'neq',
														disabled:
														[ 'boolean', ]
															.includes(restriction.fieldRestriction.dataType ?? ''),
													},
													{
														label: 'Contém',
														value: 'contains',
														disabled:
														[ 'boolean', ]
															.includes(restriction.fieldRestriction.dataType ?? ''),
													},
													{
														label: 'É falso',
														value: 'is-falsy',
													},
													{
														label: 'É verdadeiro',
														value: 'is-truly',
													},
												]}
												onSelect={(value) => {
													handleFieldRestrictionChange(
														index,
														'type',
														value
													);
												}}
											/>
										</OperatorFormItem>
										<GrowFormItem>
											<Select
												style={{ width: '100%', }}
												value={
													restriction.fieldRestriction
														.field
												}
												placeholder={'Campo...'}
												options={fieldsWithTypes?.map((el) => ({
													label: el.field,
													value: el.field,
													dataType: el.type,
												}))}
												onChange={(value, option) => {
													if (!option || Array.isArray(option))
														return;

													const dataType =
													option.dataType;
													if (dataType === 'boolean') {
														handleFieldRestrictionChange(
															index,
															'type',
															'is-truly'
														);
													}

													handleFieldRestrictionChange(
														index,
														'field',
														value
													);
													handleFieldRestrictionChange(
														index,
														'dataType',
														dataType
													);
													handleFieldRestrictionChange(
														index,
														'value',
														undefined
													);
												}}
											/>
										</GrowFormItem>
										{restriction.fieldRestriction
											.dataType === 'string' && (
											<GrowFormItem>
												<Input
													style={{ width: '100%', }}
													value={
														restriction
															.fieldRestriction
															.value
													}
													placeholder={'Valor...'}
													onChange={(e) => {
														const value =
															e.target.value;
														handleFieldRestrictionChange(
															index,
															'value',
															value
														);
													}}
												/>
											</GrowFormItem>
										)}
										{restriction.fieldRestriction
											.dataType === 'number' && (
											<GrowFormItem>
												<InputNumber
													style={{ width: '100%', }}
													value={
														restriction
															.fieldRestriction
															.value
													}
													placeholder={'Valor...'}
													onChange={(value) => {
														const _value =
															value as number;
														handleFieldRestrictionChange(
															index,
															'value',
															_value
														);
													}}
												/>
											</GrowFormItem>
										)}
										<Button
											type='text'
											danger
											icon={<MinusCircleOutlined />}
											onClick={() => {
												handleRemoveRestriction(index);
											}}
										/>
									</FieldRestrictionFormItemContainer>
								</RestrictionFormItem>
							)}
						</RestrictionFormContainer>
					</Fragment>
				);
			})}
			<Button
				disabled={disabled}
				type='dashed'
				onClick={handleAddRestriction}
			>
				Adicionar
			</Button>
		</Flex>
	);
}
